import { useEffect, useRef, useState } from "react";
import { api, fmtNGN, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { CircleNotch, Warning, CalendarCheck } from "@phosphor-icons/react";
import { MandateBadge } from "@/components/RepaymentMethodChooser";

const TONE = { paid: "badge-success", due: "badge-neutral", overdue: "badge-danger" };
const err = (e) => toast.error(formatErr(e?.response?.data?.detail) || e.message);

/** Repayment schedule, outstanding balance and penalty-free early repayment for a disbursed loan. */
export default function RepaymentPanel({ kind, appId, onChange }) {
  const [d, setD] = useState(null);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const ran = useRef(false);
  const load = () => api.get(`/repayments/${kind}/${appId}`).then((r) => setD(r.data)).catch(err);

  useEffect(() => {
    load();
    if (ran.current) return;
    ran.current = true;
    const url = new URL(window.location.href);
    const ref = url.searchParams.get("repay_ref");
    if (ref) {
      url.searchParams.delete("repay_ref");
      window.history.replaceState(null, "", url.pathname + url.search);
      api.post("/repayments/early/verify", { reference: ref }).then((r) => {
        if (r.data.ok) { toast.success("Payment received — your balance is updated"); load(); onChange?.(); }
        else toast.info("Your payment hasn't completed yet.");
      }).catch(err);
    }
  }, [kind, appId]); // eslint-disable-line

  const pay = async (full) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/repayments/${kind}/${appId}/early`, { full, amount: full ? null : Number(amount), return_url: window.location.origin + window.location.pathname });
      if (data.authorization_url) return window.location.assign(data.authorization_url);
      const { data: v } = await api.post("/repayments/early/verify", { reference: data.reference });
      toast.success(v.state === "repaid" ? "Loan fully repaid — thank you!" : `Paid ${fmtNGN(data.amount)} — balance updated`);
      setAmount(""); load(); onChange?.();
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  if (!d) return <CircleNotch size={22} className="animate-spin text-[#0A3622]" />;
  if (!d.schedule.length) return null;
  const closed = d.state === "repaid";
  return (
    <div className="space-y-5" data-testid="repayment-panel">
      {d.repayment_status === "overdue" && (
        <div className="rounded-2xl bg-[#FBE3E3]/60 border border-[#C93B3B]/40 p-4 text-sm text-[#A82F2F] flex gap-2" data-testid="repayment-overdue-banner">
          <Warning size={18} weight="fill" className="shrink-0" /> Instalment #{d.overdue_flag?.installment} is overdue after 3 attempts. Please pay below to bring your loan up to date.
        </div>
      )}
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="rounded-2xl border border-[#E5E3DB] bg-white p-4"><div className="overline">Outstanding balance</div><div className="font-display text-2xl font-semibold mt-1" data-testid="repayment-outstanding">{fmtNGN(closed ? 0 : d.outstanding)}</div></div>
        <div className="rounded-2xl border border-[#E5E3DB] bg-white p-4"><div className="overline">Pay off today</div><div className="font-display text-2xl font-semibold mt-1" data-testid="repayment-payoff">{fmtNGN(d.payoff)}</div><div className="text-[11px] text-[#4F5D4F]">No early-repayment penalty</div></div>
        <div className="rounded-2xl border border-[#E5E3DB] bg-white p-4"><div className="overline">Repaying by</div><div className="mt-2"><MandateBadge method={d.method} /></div></div>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-[#E5E3DB] bg-white">
        <table className="w-full text-sm" data-testid="repayment-schedule">
          <thead><tr className="text-left text-[11px] uppercase tracking-[0.12em] text-[#4F5D4F] border-b border-[#E5E3DB]">
            <th className="py-2 px-4">#</th><th className="py-2 px-4">Due date</th><th className="py-2 px-4 text-right">Amount</th><th className="py-2 px-4 text-right">Balance after</th><th className="py-2 px-4">Status</th>
          </tr></thead>
          <tbody>
            {d.schedule.map((x) => (
              <tr key={x.installment} className="border-b border-[#F0EFEA]" data-testid={`repayment-row-${x.installment}`}>
                <td className="py-2.5 px-4 font-mono">{x.installment}</td>
                <td className="py-2.5 px-4">{x.due_date}</td>
                <td className="py-2.5 px-4 text-right font-mono">{fmtNGN(x.amount)}{x.paid_amount > 0 && x.status !== "paid" ? <div className="text-[10px] text-[#4F5D4F]">{fmtNGN(x.paid_amount)} paid</div> : null}</td>
                <td className="py-2.5 px-4 text-right font-mono">{fmtNGN(x.balance_after)}</td>
                <td className="py-2.5 px-4"><span className={`badge ${TONE[x.status] || "badge-neutral"}`}>{x.status === "paid" ? (x.settled_early ? "Paid early" : "Paid") : x.status === "overdue" ? "Overdue" : x.attempts ? `Retrying (${x.attempts}/3)` : "Upcoming"}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {closed ? (
        <div className="rounded-2xl bg-[#E2F3EB]/50 border border-[#2D7A5D]/30 p-4 text-sm text-[#1F6B4C] flex items-center gap-2" data-testid="repayment-closed"><CalendarCheck size={18} weight="fill" /> Fully repaid and closed. Thank you!</div>
      ) : (
        <div className="rounded-2xl border border-[#E5E3DB] bg-white p-5" data-testid="early-repayment">
          <h4 className="font-display text-lg font-medium">Repay early</h4>
          <p className="text-xs text-[#4F5D4F] mt-1 mb-3">Pay any amount now by card or bank transfer. Extra payments reduce what you owe and shrink your remaining instalments — no penalty.</p>
          <div className="flex flex-wrap gap-2">
            <input type="number" min="100" className="input max-w-[200px]" placeholder="Amount (₦)" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="early-amount-input" />
            <button onClick={() => pay(false)} disabled={busy || !(Number(amount) >= 100)} className="btn-outline disabled:opacity-50" data-testid="early-pay-partial">Pay this amount</button>
            <button onClick={() => pay(true)} disabled={busy} className="btn-primary" data-testid="early-pay-full">{busy ? <CircleNotch size={16} className="animate-spin" /> : `Pay off ${fmtNGN(d.payoff)}`}</button>
          </div>
        </div>
      )}
    </div>
  );
}
